import React, { useState, useEffect } from "react";
import { Control, Controller, UseFormReset, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { PartnerMemberCustomFieldModel } from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseButton, BaseInput, BaseSelect, BaseTooltip } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import {
  CUSTOM_FIELD_NAME_DUPLICATED_ERROR_TEXT,
  CUSTOM_FIELD_OPTION_DUPLICATED_ERROR_TEXT,
  CUSTOM_FIELD_SLUG_ERROR_TEXT,
  CUSTOM_FIELD_SLUG_MAX_LENGTH,
  CUSTOM_FIELD_SLUG_PATTERN,
  CUSTOM_FIELD_SLUG_TOOLTIP,
  FIELD_TYPE_OPTIONS,
} from "../../userAdditionalInfo-constant";

type Props = {
  control: Control<PartnerMemberCustomFieldModel>;
  customFieldList: PartnerMemberCustomFieldModel[]; // 필드명 중복 검증 대상 (기등록 필드)
  watch: UseFormWatch<PartnerMemberCustomFieldModel>;
  setValue: UseFormSetValue<PartnerMemberCustomFieldModel>;
  reset: UseFormReset<PartnerMemberCustomFieldModel>;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

/**
 * 이용자 부가정보 > 커스텀 필드 설정 모달
 * 계약그룹 CustomFieldFormModal 복제 (필드명 / 필드유형 / 옵션 - 기능 동일)
 * 필드유형은 최초 생성 시에만 선택 가능, 수정 시 disabled
 */
const CustomFieldFormModal = ({ control, customFieldList, watch, setValue, reset, onClose, onSubmit }: Props) => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <BaseAbstractModal size="large" isOpen={true}>
      <form onSubmit={onSubmit}>
        <section className="base-abstract-modal__title flex-center-between border-bottom-gray mb20 ">
          <h1>커스텀 필드 설정</h1>
        </section>
        <div className="px30">
          {/* 물리명 (ESL 연동용 slug) - 선택 입력, 영문/숫자/_ 50자 */}
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <div className="flex-center-start">
                <p>물리명</p>
                <BaseTooltip className="ml2" contents={CUSTOM_FIELD_SLUG_TOOLTIP} touchIcon="QUESTION" />
              </div>
            </div>
            <div className="contents-container__grid-contents minmax240">
              <Controller
                control={control}
                name="slug"
                rules={{
                  pattern: { value: CUSTOM_FIELD_SLUG_PATTERN, message: CUSTOM_FIELD_SLUG_ERROR_TEXT },
                }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                  return (
                    <BaseInput
                      onChange={onChange}
                      value={value ?? ""}
                      name={name}
                      maxLength={CUSTOM_FIELD_SLUG_MAX_LENGTH}
                      placeholder="물리명을 입력해 주세요"
                      errorText={error?.message}
                    />
                  );
                }}
              />
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p className="required">필드명</p>
            </div>
            <div className="contents-container__grid-contents minmax240">
              <Controller
                control={control}
                name="fieldName"
                rules={{
                  required: "필수입력 항목입니다.",
                  // 기등록 필드와 필드명 중복 불가 - 수정 시 자기 자신은 제외 (신규는 fieldId=0 이므로 전체가 비교 대상)
                  validate: (value, formValues) =>
                    !customFieldList.some(
                      (field) => field.fieldId !== formValues.fieldId && !field.isDeleted && field.fieldName?.trim() === value?.trim(),
                    ) || CUSTOM_FIELD_NAME_DUPLICATED_ERROR_TEXT,
                }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                  return <BaseInput onChange={onChange} value={value} name={name} placeholder="필드명을 입력해 주세요" errorText={error?.message} />;
                }}
              />
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p className="required">필드 유형</p>
            </div>
            <div className="contents-container__grid-contents minmax240">
              <Controller
                control={control}
                name="fieldType"
                rules={{ required: "필수입력 항목입니다." }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                  return (
                    <BaseSelect
                      stateOptions={FIELD_TYPE_OPTIONS}
                      setStateValue={onChange}
                      value={value}
                      errorText={error?.message}
                      isDisabled={watch("fieldId") !== 0}
                    />
                  );
                }}
              />
            </div>
          </section>

          {(watch("fieldType") === "RADIO" || watch("fieldType") === "CHECKBOX") && (
            <div
              className="px20 py12" //
              style={{
                background: "#F8F8FA",
                borderRadius: "10px",
                overflowY: "auto",
                maxHeight: `min(825px, ${windowHeight * 0.4}px)`,
              }}
            >
              {watch("optionList")?.map((item, index) => (
                <Controller
                  key={item.orderNum}
                  control={control}
                  name={`optionList.${index}.optionName`}
                  rules={{
                    required: !item.isDeleted ? "필수입력 항목입니다." : false,
                    // 같은 필드 내 옵션명 중복 불가 (다른 필드의 옵션명과는 중복 허용)
                    // 중복 쌍 중 뒤쪽 항목에만 에러를 노출해 수정 대상을 명확히 한다
                    validate: (value, formValues) => {
                      const optionList = formValues.optionList || [];
                      if (optionList[index]?.isDeleted) return true;

                      const isDuplicated = optionList
                        .slice(0, index)
                        .some((option) => !option.isDeleted && option.optionName?.trim() === value?.trim());

                      return !isDuplicated || CUSTOM_FIELD_OPTION_DUPLICATED_ERROR_TEXT;
                    },
                  }}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                    return (
                      <>
                        {!item.isDeleted && (
                          <section className="contents-container__grid " key={item.orderNum}>
                            <div className="contents-container__grid-index">
                              <p className="required">
                                옵션
                                {(watch("optionList") || []).filter((item) => !item.isDeleted).findIndex((opt) => opt.orderNum === item.orderNum) + 1}
                              </p>
                            </div>
                            <div className="contents-container__grid-contents">
                              <div className=" flex-direction-start flex-row flex-center-start ">
                                <BaseInput
                                  name={name}
                                  className="minmax240"
                                  placeholder="옵션명을 입력해 주세요"
                                  value={value}
                                  onChange={onChange}
                                  // errorText={error?.message}
                                />
                                {(() => {
                                  const visibleOptions = watch("optionList")?.filter((item) => !item.isDeleted) || [];
                                  const isFirstItem = visibleOptions.findIndex((opt) => opt.orderNum === item.orderNum) === 0;
                                  const canDelete = visibleOptions.length > 1;

                                  return isFirstItem ? (
                                    <>
                                      <button
                                        className="minus-btn ml10"
                                        disabled={!canDelete}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (!canDelete) return;

                                          setValue(
                                            "optionList",
                                            watch("optionList")!.map((item, i) => {
                                              if (i === index) {
                                                return { ...item, isDeleted: true };
                                              }
                                              return item;
                                            }),
                                          );
                                        }}
                                      />
                                      <button
                                        className="plus-btn ml10"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setValue("optionList", [
                                            ...(watch("optionList") || []),
                                            {
                                              optionId: 0,
                                              optionName: "",
                                              isDeleted: false,
                                              orderNum: Math.max(...(watch("optionList")?.map((item) => item.orderNum || 0) || [0])) + 1,
                                            },
                                          ]);
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <button
                                      className="minus-btn ml10"
                                      disabled={!canDelete}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!canDelete) return;

                                        const currentOptions = watch("optionList")!;
                                        const targetOption = currentOptions[index];

                                        if (targetOption.optionId && targetOption.optionId !== 0) {
                                          // 기존 데이터: isDeleted true로 설정
                                          setValue(
                                            "optionList",
                                            currentOptions.map((item, i) => {
                                              if (i === index) {
                                                return { ...item, isDeleted: true };
                                              }
                                              return item;
                                            }),
                                          );
                                        } else {
                                          // 새로 추가된 데이터: filter로 완전 삭제
                                          setValue(
                                            "optionList",
                                            currentOptions.filter((_, i) => i !== index),
                                          );
                                        }
                                      }}
                                    />
                                  );
                                })()}
                              </div>
                              {error?.message && <p className="validation-text">{error?.message}</p>}
                            </div>
                          </section>
                        )}
                      </>
                    );
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <section className="base-abstract-modal__btn-wrap">
          <BaseButton
            className="color-white"
            title="취소"
            onClick={() => {
              onClose();
              reset();
            }}
          />
          <BaseButton title="저장" type="submit" />
        </section>
      </form>
    </BaseAbstractModal>
  );
};

export default CustomFieldFormModal;
